/**
 * SettingsModal — website settings (colors, fonts, effects, tracking, legal)
 */
import { useBuilderStore } from '@/state/store';
import { eliteStylePresets } from '@/data/elite-presets';
import { fontStacks } from '@/data/font-stacks';
import { Toast } from '@/state/toast';
import { Modal } from './Modal';
import { EditorField } from '@/components/editor/EditorField';
import { EditorTextarea } from '@/components/editor/EditorTextarea';
import { EditorCheckbox } from '@/components/editor/EditorCheckbox';
import { EditorSelect } from '@/components/editor/EditorSelect';
import { EditorColorPicker } from '@/components/editor/EditorColorPicker';

export function SettingsModal() {
  const openModal = useBuilderStore((s) => s.openModal);
  const setOpenModal = useBuilderStore((s) => s.setOpenModal);
  const settings = useBuilderStore((s) => s.settings);
  const updateSettings = useBuilderStore((s) => s.updateSettings);

  const isOpen = openModal === 'settings';
  const onClose = () => setOpenModal(null);
  const onUpdate = (s: any) => updateSettings(s);

  const trackingPreset = settings.trackingPreset || "none";
  const tracking = settings.tracking || {};
  const ga4Ok = !tracking.ga4MeasurementId || /^G-[A-Z0-9]+$/i.test(String(tracking.ga4MeasurementId).trim());
  const matomoUrlOk = !tracking.matomoUrl || /^https?:///i.test(String(tracking.matomoUrl).trim());
  const matomoIdOk = !tracking.matomoSiteId || /^d+$/.test(String(tracking.matomoSiteId).trim());

  const updateTrackingPreset = (preset: string) => {
    onUpdate({ ...settings, trackingPreset: preset, tracking: { ...tracking } });
  };

  const updateTracking = (patch: any) => {
    onUpdate({ ...settings, trackingPreset, tracking: { ...tracking, ...patch } });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Website-Einstellungen"
      footer={<button className="btn btn-primary" onClick={onClose}>Schliessen</button>}
    >
      <h4 style={{ marginBottom: 12, fontSize: 13, color: "var(--builder-text-muted)", textTransform: "uppercase" }}>Allgemein</h4>
      <EditorField label="Seitenname" value={settings.siteName} onChange={(v) => onUpdate({ ...settings, siteName: v })} />
      <EditorTextarea label="SEO Beschreibung" value={settings.siteDescription} onChange={(v) => onUpdate({ ...settings, siteDescription: v })} rows={2} />
      <EditorField label="Favicon Emoji" value={settings.faviconEmoji} onChange={(v) => onUpdate({ ...settings, faviconEmoji: v })} placeholder={"🏢"} />

      <h4 style={{ marginTop: 24, marginBottom: 12, fontSize: 13, color: "var(--builder-text-muted)", textTransform: "uppercase" }}>Farben</h4>
      <EditorColorPicker label="Primärfarbe" value={settings.primaryColor} onChange={(v) => onUpdate({ ...settings, primaryColor: v })} bgColor={(eliteStylePresets[settings.stylePreset] || eliteStylePresets.signature).neutrals.bg} />
      <EditorColorPicker label="Akzentfarbe" value={settings.accentColor} onChange={(v) => onUpdate({ ...settings, accentColor: v })} bgColor={(eliteStylePresets[settings.stylePreset] || eliteStylePresets.signature).neutrals.bg} />

      <div style={{ display: "flex", gap: 10, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button className="btn btn-secondary" type="button" onClick={() => setOpenModal('colorGen')}>
          Color Generator öffnen
        </button>
        {settings.colorStack ? (
          <span style={{ fontSize: 12, color: "var(--builder-text-muted)" }}>
            Color Stack übernommen. Overrides: {settings.useColorStack ? "aktiv" : "inaktiv"}.
          </span>
        ) : (
          <span style={{ fontSize: 12, color: "var(--builder-text-muted)" }}>
            Tipp: Erzeuge eine Palette aus Logo/Farben und übernimm sie in den Builder.
          </span>
        )}
      </div>

      {settings.colorStack && (
        <div style={{ marginTop: 10 }}>
          <EditorField label="Color Stack Overrides aktiv?" value={settings.useColorStack ? "ja" : "nein"} onChange={(v) => onUpdate({ ...settings, useColorStack: String(v).toLowerCase() === "ja" })} placeholder="ja/nein" />
        </div>
      )}

      <h4 style={{ marginTop: 24, marginBottom: 12, fontSize: 13, color: "var(--builder-text-muted)", textTransform: "uppercase" }}>Elite Design</h4>
      <EditorSelect
        label="Design-Stil (Preset)"
        value={settings.stylePreset || "signature"}
        onChange={(v) => onUpdate({ ...settings, stylePreset: v })}
        options={Object.entries(eliteStylePresets).map(([key, preset]) => ({ value: key, label: `${preset.name} – ${preset.description}` }))}
      />
      <div style={{ height: 8 }} />
      <EditorSelect
        label="Layout-Dichte"
        value={settings.designDensity || "balanced"}
        onChange={(v) => onUpdate({ ...settings, designDensity: v })}
        options={[
          { value: "compact", label: "Compact – wenig Weissraum (für datenreiche Seiten)" },
          { value: "balanced", label: "Balanced – Standard (empfohlen)" },
          { value: "airy", label: "Airy – viel Weissraum (Premium-Look)" },
        ]}
      />
      <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
        <button className="btn btn-sm" onClick={() => {
          const preset = eliteStylePresets[settings.stylePreset || "signature"] || eliteStylePresets.signature;
          const next = {
            ...settings,
            darkMode: typeof preset.defaults?.darkMode === "boolean" ? preset.defaults.darkMode : settings.darkMode,
            fontStack: preset.defaults?.fontStack || settings.fontStack,
          };
          onUpdate(next);
          Toast.success("Elite-Defaults angewendet");
        }}>
          Elite-Defaults anwenden
        </button>
        <span style={{ fontSize: 12, color: "var(--builder-text-muted)" }}>Setzt automatisch Dark Mode & Schrift passend zum Preset.</span>
      </div>

      <h4 style={{ marginTop: 24, marginBottom: 12, fontSize: 13, color: "var(--builder-text-muted)", textTransform: "uppercase" }}>
        {"✨"} Premium Effekte
      </h4>
      <div style={{ display: "grid", gap: 8 }}>
        <EditorCheckbox label="Gradient Orbs (animierte Farbkreise)" checked={settings.fxOrbs} onChange={(v) => onUpdate({ ...settings, fxOrbs: v })} />
        <EditorCheckbox label="Grain Texture (subtile Körnung)" checked={settings.fxGrain} onChange={(v) => onUpdate({ ...settings, fxGrain: v })} />
        <EditorCheckbox label="Glassmorphism Cards" checked={settings.fxGlass} onChange={(v) => onUpdate({ ...settings, fxGlass: v })} />
      </div>
      {(settings.fxOrbs || settings.fxGrain || settings.fxGlass) && (
        <div style={{ marginTop: 12 }}>
          <EditorSelect
            label="Effekt-Intensität"
            value={settings.fxIntensity || "medium"}
            onChange={(v) => onUpdate({ ...settings, fxIntensity: v })}
            options={[
              { value: "subtle", label: "Subtle – Kaum sichtbar, sehr dezent" },
              { value: "medium", label: "Medium – Ausgewogen (empfohlen)" },
              { value: "intense", label: "Intense – Stark sichtbar, auffällig" },
            ]}
          />
        </div>
      )}

      <h4 style={{ marginTop: 24, marginBottom: 12, fontSize: 13, color: "var(--builder-text-muted)", textTransform: "uppercase" }}>Darstellung</h4>
      <EditorCheckbox label="Dark Mode" checked={settings.darkMode} onChange={(v) => onUpdate({ ...settings, darkMode: v })} />
      <div style={{ height: 8 }} />
      <EditorCheckbox label="Dark Mode Toggle anzeigen (für Besucher)" checked={settings.darkModeToggle} onChange={(v) => onUpdate({ ...settings, darkModeToggle: v })} />
      <div style={{ height: 8 }} />
      <EditorSelect
        label="Schriftart"
        value={settings.fontStack}
        onChange={(v) => onUpdate({ ...settings, fontStack: v })}
        options={Object.entries(fontStacks).map(([key, stack]) => ({ value: key, label: `${(stack as any).name} – ${(stack as any).description}` }))}
      />

      <h4 style={{ marginTop: 24, marginBottom: 12, fontSize: 13, color: "var(--builder-text-muted)", textTransform: "uppercase" }}>Zusätzliche Seiten</h4>
      <EditorCheckbox label="Impressum generieren" checked={settings.generateImpressum} onChange={(v) => onUpdate({ ...settings, generateImpressum: v })} />
      <div style={{ height: 8 }} />
      <EditorCheckbox label="Datenschutz generieren" checked={settings.generateDatenschutz} onChange={(v) => onUpdate({ ...settings, generateDatenschutz: v })} />
      <div style={{ height: 8 }} />
      <EditorCheckbox label="AGB generieren" checked={settings.generateAgb} onChange={(v) => onUpdate({ ...settings, generateAgb: v })} />
      {settings.generateAgb && (
        <div style={{ marginTop: 8, display: 'grid', gap: 8, paddingLeft: 16 }}>
          <EditorField label="Gerichtsstand" value={settings.agb?.gerichtsstand || 'Bern'} onChange={(v) => onUpdate({ ...settings, agb: { ...settings.agb, gerichtsstand: v } })} placeholder="Bern" />
          <EditorField label="Zahlungsfrist" value={settings.agb?.zahlungsfrist || '30 Tage'} onChange={(v) => onUpdate({ ...settings, agb: { ...settings.agb, zahlungsfrist: v } })} placeholder="30 Tage" />
        </div>
      )}
      <div style={{ height: 16 }} />
      <EditorCheckbox label="Angebot-Seite generieren" checked={settings.generateAngebot} onChange={(v) => onUpdate({ ...settings, generateAngebot: v })} />
      <div style={{ height: 8 }} />
      <EditorCheckbox label="Methoden-Seite generieren" checked={settings.generateMethoden} onChange={(v) => onUpdate({ ...settings, generateMethoden: v })} />
      <div style={{ height: 8 }} />
      <EditorCheckbox label="Über-mich-Seite generieren" checked={settings.generateUeberMich} onChange={(v) => onUpdate({ ...settings, generateUeberMich: v })} />

      {(settings.generateAngebot || settings.generateMethoden || settings.generateUeberMich) && (
        <p style={{ marginTop: 12, fontSize: 12, color: 'var(--builder-text-muted)', lineHeight: 1.5 }}>
          {"💡"} Seiteninhalte bearbeiten Sie direkt über die <strong>Seiten-Tabs</strong> in der Sidebar.
        </p>
      )}

      <h4 style={{ marginTop: 24, marginBottom: 12, fontSize: 13, color: "var(--builder-text-muted)", textTransform: "uppercase" }}>Tracking & Datenschutz (CH)</h4>
      <div className="field">
        <label className="field-label">Tracking Preset</label>
        <div style={{ display: "grid", gap: 8 }}>
          {[
            { value: 'none', title: 'No Tracking (empfohlen)', desc: 'Kein Analytics. Keine Requests.' },
            { value: 'ga4', title: 'GA4 (Opt-in / Statistik)', desc: 'Wird erst nach Consent geladen.' },
            { value: 'matomo', title: 'Matomo Self-Host (Opt-in / Statistik)', desc: 'Wird erst nach Consent geladen.' },
          ].map((opt) => (
            <label key={opt.value} style={{ display: "flex", gap: 10, padding: "10px 12px", border: "1px solid var(--builder-border)", borderRadius: 12, background: "var(--builder-surface)", cursor: "pointer" }}>
              <input type="radio" name="trackingPreset" checked={trackingPreset === opt.value} onChange={() => updateTrackingPreset(opt.value)} />
              <div style={{ display: "grid", gap: 2 }}>
                <div style={{ fontSize: 13, fontWeight: 650, color: "var(--builder-text)" }}>{opt.title}</div>
                <div style={{ fontSize: 12, color: "var(--builder-text-muted)" }}>{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {trackingPreset === "ga4" && (
        <div style={{ marginTop: 10 }}>
          <EditorField label="GA4 Measurement ID" value={tracking.ga4MeasurementId} onChange={(v) => updateTracking({ ga4MeasurementId: v })} placeholder="G-XXXXXXXXXX" />
          {!ga4Ok && <div style={{ marginTop: 6, fontSize: 12, color: "var(--builder-warning, #b45309)" }}>Soft-Check: GA4 ID sollte mit <b>G-</b> beginnen.</div>}
        </div>
      )}

      {trackingPreset === "matomo" && (
        <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
          <EditorField label="Matomo Base URL" value={tracking.matomoUrl} onChange={(v) => updateTracking({ matomoUrl: v })} placeholder="https://analytics.example.com" />
          {!matomoUrlOk && <div style={{ marginTop: -4, fontSize: 12, color: "var(--builder-warning, #b45309)" }}>Soft-Check: URL sollte mit <b>https://</b> beginnen.</div>}
          <EditorField label="Matomo Site ID" value={tracking.matomoSiteId} onChange={(v) => updateTracking({ matomoSiteId: v })} placeholder="1" />
          {!matomoIdOk && <div style={{ marginTop: -4, fontSize: 12, color: "var(--builder-warning, #b45309)" }}>Soft-Check: Site ID sollte numerisch sein.</div>}
        </div>
      )}

      {(settings.generateImpressum || settings.generateDatenschutz || settings.generateAgb) && (
        <>
          <h4 style={{ marginTop: 24, marginBottom: 12, fontSize: 13, color: "var(--builder-text-muted)", textTransform: "uppercase" }}>Rechtliche Angaben</h4>
          <EditorField label="Firmenname (rechtlich)" value={settings.companyLegal?.name} onChange={(v) => onUpdate({ ...settings, companyLegal: { ...settings.companyLegal, name: v } })} />
          <EditorField label="Inhaber" value={settings.companyLegal?.owner} onChange={(v) => onUpdate({ ...settings, companyLegal: { ...settings.companyLegal, owner: v } })} />
          <EditorField label="Adresse" value={settings.companyLegal?.address} onChange={(v) => onUpdate({ ...settings, companyLegal: { ...settings.companyLegal, address: v } })} />
          <EditorField label="UID-Nummer" value={settings.companyLegal?.uid} onChange={(v) => onUpdate({ ...settings, companyLegal: { ...settings.companyLegal, uid: v } })} placeholder="CHE-123.456.789" />
        </>
      )}
    </Modal>
  );
}
